import { Injectable } from '@angular/core';

export interface ModalPreference {
  dontShowAgain: boolean;
  lastDismissed?: string;
  dismissCount?: number;
  expiresAt?: string; // Optional: reset after certain time
}

export interface ModalPreferences {
  [modalId: string]: ModalPreference;
}

@Injectable({
  providedIn: 'root'
})
export class ModalPreferencesService {
  private readonly STORAGE_KEY = 'modal_preferences';

  // Get all preferences
  private getPreferences(): ModalPreferences {
    const data = localStorage.getItem(this.STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  }

  // Save all preferences
  private savePreferences(preferences: ModalPreferences): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(preferences));
  }

  // Check if modal should be shown
  shouldShowModal(modalId: string): boolean {
    const preferences = this.getPreferences();
    const modalPref = preferences[modalId];

    if (!modalPref) {
      return true; // Not set, show modal
    }

    // Check if expired (optional)
    if (modalPref.expiresAt) {
      const expired = new Date(modalPref.expiresAt) < new Date();
      if (expired) {
        this.resetModalPreference(modalId);
        return true;
      }
    }

    return !modalPref.dontShowAgain;
  }

  // Set "don't show again"
  setDontShowAgain(modalId: string, dontShow: boolean = true): void {
    const preferences = this.getPreferences();
    
    preferences[modalId] = {
      dontShowAgain: dontShow,
      lastDismissed: new Date().toISOString(),
      dismissCount: (preferences[modalId]?.dismissCount || 0) + 1
    };

    this.savePreferences(preferences);
  }

  // Set with expiration (e.g., don't show for 30 days)
  setDontShowAgainWithExpiry(modalId: string, days: number): void {
    const preferences = this.getPreferences();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + days);

    preferences[modalId] = {
      dontShowAgain: true,
      lastDismissed: new Date().toISOString(),
      expiresAt: expiresAt.toISOString()
    };

    this.savePreferences(preferences);
  }

  // Reset preference
  resetModalPreference(modalId: string): void {
    const preferences = this.getPreferences();
    delete preferences[modalId];
    this.savePreferences(preferences);
  }

  // Clear all preferences
  clearAllPreferences(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Get preference details
  getModalPreference(modalId: string): ModalPreference | null {
    const preferences = this.getPreferences();
    return preferences[modalId] || null;
  }
}